import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useLoading } from '../context/LoadingContext';

gsap.registerPlugin(ScrollTrigger);

const ScrollIndicator = React.memo(() => {
  const arrowRef = useRef(null);
  const timelineRef = useRef(null);
  const { isInitialized } = useLoading();

  // Memoize scroll handler
  const handleScrollClick = useCallback(() => {
    gsap.to(window, {
      duration: 1,
      scrollTo: window.innerHeight,
      ease: "power2.inOut"
    });
  }, []);

  useEffect(() => {
    if (!isInitialized) return;

    const arrow = arrowRef.current;
    if (!arrow) return;

    // Set initial state with GPU acceleration
    gsap.set(arrow, {
      opacity: 1,
      y: 0,
      force3D: true
    });

    // Create optimized bounce animation
    timelineRef.current = gsap.timeline({
      repeat: -1,
      defaults: { ease: "power1.inOut" }
    });

    timelineRef.current
      .to(arrow, {
        y: 10, // Reduced movement for performance
        duration: 1.5 // Slower animation for less CPU usage
      })
      .to(arrow, {
        y: 0,
        duration: 1.5
      });

    // Optimized fade out on scroll
    ScrollTrigger.create({
      trigger: "body",
      start: "top top",
      end: "15% top", // Larger range for smoother transition
      scrub: 1,
      onUpdate: (self) => {
        gsap.to(arrow, {
          opacity: 1 - self.progress,
          y: self.progress * 50,
          duration: 0.1,
          overwrite: "auto"
        });
      }
    });

    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger === document.body) {
          trigger.kill();
        }
      });
    };
  }, [isInitialized]);

  return (
    <div 
      ref={arrowRef}
      className="absolute bottom-8 left-1/2 transform -translate-x-1/2 cursor-pointer"
      onClick={handleScrollClick}
      style={{ willChange: 'transform, opacity' }}
    >
      <div className="flex flex-col items-center">
        <ChevronDownIcon className="h-8 w-8 text-white" />
        <span className="text-sm font-medium text-white mt-2">Scroll Down</span>
      </div>
    </div>
  );
});

ScrollIndicator.displayName = 'ScrollIndicator';

export default ScrollIndicator; 